import { Helmet } from "react-helmet-async";

export const SITE_URL = "https://themodernexit.net";
export const SITE_NAME = "The Modern Exit";

/** Canonical per-route SEO config. Keep copy under these hard limits:
 *   - title: ≤ 60 chars (Google truncates ~60)
 *   - description: ≤ 160 chars (Google truncates ~155-160)
 *
 *  Every entry includes `path` so we can also generate sitemap.xml from
 *  this single source of truth. */
export const SEO_CONFIG = {
  "/": {
    path: "/",
    title: "The Modern Exit · Turn Service Leads Into Booked Revenue",
    description:
      "Websites and lead-to-cash systems for service businesses. Capture, qualify, route, follow up, book, and collect — so revenue stops leaking between inquiry and payment.",
  },
  "/websites": {
    path: "/websites",
    title: "Website Packages · Built to Convert | The Modern Exit",
    description:
      "Premium service-business websites — Starter, Growth, and Premium tiers. Mobile-first, SEO-ready on day one, built to turn visitors into leads.",
  },
  "/revenue-systems": {
    path: "/revenue-systems",
    title: "AI Revenue Systems · Lead-to-Cash | The Modern Exit",
    description:
      "Done-for-you AI revenue systems — CRM, follow-up, booking, payments, reviews, reactivation. We fix the flow between lead and paid customer.",
  },
  "/packages": {
    path: "/packages",
    title: "Packages · Websites + Revenue Systems | The Modern Exit",
    description:
      "Two paths, one studio. Compare Website Packages and Revenue System Packages, then pick the build that matches the leak you need to fix.",
  },
  "/pricing": {
    path: "/pricing",
    title: "Pricing · Websites + Revenue Systems | The Modern Exit",
    description:
      "Transparent pricing for Modern Exit websites and AI revenue systems. Flat-fee builds and monthly operating tiers — no retainers, no surprises.",
  },
  "/faq": {
    path: "/faq",
    title: "FAQ · Modern Exit Websites & Revenue Systems",
    description:
      "Common questions about Modern Exit website builds, AI revenue systems, timelines, pricing, ownership, and how the lead-to-cash flow works.",
  },
  "/revenue-leak-audit": {
    path: "/revenue-leak-audit",
    title: "Revenue Leak Audit · The Modern Exit",
    description:
      "Find where your leads turn cold. The Revenue Leak Audit shows where prospects stall between inquiry, booking, payment, follow-up, and revenue visibility.",
  },
  "/insights": {
    path: "/insights",
    title: "Insights · Modern Exit Revenue Systems Library",
    description:
      "Premium insights for service businesses turning websites, leads, and follow-up into booked revenue. Strategy, automation, and CRM playbooks from the studio that builds them.",
  },
  "/build-brief": {
    path: "/build-brief",
    title: "Start My Website Build · The Modern Exit",
    description:
      "Share your Website Build Brief. A short, structured intake that gives our team everything needed to ship a premium, conversion-ready website.",
    noindex: true, // transactional form, no SEO benefit
  },
  "/privacy": {
    path: "/privacy",
    title: "Privacy Policy · The Modern Exit",
    description:
      "How The Modern Exit collects, uses, and protects your information — lead forms, cookies, analytics, payments, and your rights as a visitor.",
  },
  "/terms": {
    path: "/terms",
    title: "Terms of Service · The Modern Exit",
    description:
      "The terms that govern your use of The Modern Exit website and services — engagement scope, payments, intellectual property, and liability.",
  },
  "/thank-you": {
    path: "/thank-you",
    title: "Thank You · The Modern Exit",
    description:
      "Thanks for reaching out. A member of the Modern Exit team will be in touch shortly to move your project forward.",
    noindex: true,
  },
};

/** Drop-in SEO block for any page. Usage: `<SEO page="/websites" />` or
 *  `<SEO title="…" description="…" path="/custom-route" />`. */
export default function SEO({ page, title, description, path, noindex }) {
  const cfg = (page && SEO_CONFIG[page]) || {};
  const t = title || cfg.title || "The Modern Exit";
  const d = description || cfg.description || "";
  const p = path || cfg.path || "/";
  const canonical = `${SITE_URL}${p === "/" ? "" : p}`;
  const robots = noindex ?? cfg.noindex ? "noindex, nofollow" : "index, follow";
  return (
    <Helmet>
      <title>{t}</title>
      <meta name="description" content={d} />
      <meta name="robots" content={robots} />
      <link rel="canonical" href={canonical} />
      <meta property="og:title" content={t} />
      <meta property="og:description" content={d} />
      <meta property="og:url" content={canonical} />
      <meta name="twitter:title" content={t} />
      <meta name="twitter:description" content={d} />
    </Helmet>
  );
}
